import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { reportError } from '@/lib/error-reporting';
import { z } from 'zod';
import type { DiscoveryMethod } from '@/components/values/DiscoveryMethodSelector';

// --- Draft data schemas (version 1) ---

const cardSortDraftSchema = z.object({
  schema_version: z.literal(1),
  categories: z.record(z.enum(['unassigned', 'very', 'somewhat', 'not'])),
  step: z.enum(['sort', 'narrow']),
  topFive: z.array(z.string()),
});

const reflectionDraftSchema = z.object({
  schema_version: z.literal(1),
  answers: z.array(z.string()),
  currentPrompt: z.number(),
});

const quickAssessmentDraftSchema = z.object({
  schema_version: z.literal(1),
  ratings: z.record(z.number()),
  currentIndex: z.number(),
  showResults: z.boolean(),
});

const chatMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
});

const chatDraftSchema = z.object({
  schema_version: z.literal(1),
  messages: z.array(chatMessageSchema).max(50),
});

const DRAFT_SCHEMAS: Record<DiscoveryMethod, z.ZodSchema> = {
  'card-sort': cardSortDraftSchema,
  'reflection': reflectionDraftSchema,
  'assessment': quickAssessmentDraftSchema,
  'chat': chatDraftSchema,
};

export type CardSortDraftData = z.infer<typeof cardSortDraftSchema>;
export type ReflectionDraftData = z.infer<typeof reflectionDraftSchema>;
export type QuickAssessmentDraftData = z.infer<typeof quickAssessmentDraftSchema>;
export type ChatDraftData = z.infer<typeof chatDraftSchema>;

export interface ValueAssessmentDraft {
  id: string;
  method: DiscoveryMethod;
  draft_data: Record<string, unknown>;
  assessment_id: string | null;
  created_at: string;
  updated_at: string;
}

const DEBOUNCE_MS = 2000;

export const useValueAssessmentDraft = () => {
  const { user } = useAuth();
  const [draft, setDraft] = useState<ValueAssessmentDraft | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSave = useRef<{
    method: DiscoveryMethod;
    data: Record<string, unknown>;
    assessmentId?: string | null;
  } | null>(null);

  // Load existing draft on mount
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const loadDraft = async () => {
      try {
        const { data, error } = await supabase
          .from('value_assessment_drafts')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          reportError(error, 'values-assessment', { action: 'load-draft' });
        } else if (data) {
          // Validate draft data against schema
          const method = data.method as DiscoveryMethod;
          const schema = DRAFT_SCHEMAS[method];
          if (schema) {
            const result = schema.safeParse(data.draft_data);
            if (result.success) {
              setDraft(data as ValueAssessmentDraft);
            } else {
              // Invalid/outdated draft — discard silently
              console.warn('Discarding invalid draft:', result.error.message);
              await deleteDraftFromDB(user.id);
            }
          }
        }
      } catch (err) {
        reportError(err, 'values-assessment', { action: 'load-draft' });
      } finally {
        setIsLoading(false);
      }
    };

    loadDraft();
  }, [user]);

  // Flush pending saves on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (pendingSave.current && user) {
        // Fire-and-forget flush
        upsertDraft(
          user.id,
          pendingSave.current.method,
          pendingSave.current.data,
          pendingSave.current.assessmentId
        );
        pendingSave.current = null;
      }
    };
  }, [user]);

  const upsertDraft = async (
    userId: string,
    method: DiscoveryMethod,
    data: Record<string, unknown>,
    assessmentId?: string | null
  ) => {
    try {
      const payload: Record<string, unknown> = {
        user_id: userId,
        method,
        draft_data: data,
      };
      if (assessmentId !== undefined) {
        payload.assessment_id = assessmentId;
      }

      const { error } = await supabase
        .from('value_assessment_drafts')
        .upsert(payload as any, { onConflict: 'user_id' });

      if (error) {
        reportError(error, 'values-assessment', { action: 'save-draft' });
      }
    } catch (err) {
      reportError(err, 'values-assessment', { action: 'save-draft' });
    }
  };

  const deleteDraftFromDB = async (userId: string) => {
    try {
      await supabase
        .from('value_assessment_drafts')
        .delete()
        .eq('user_id', userId);
    } catch (err) {
      reportError(err, 'values-assessment', { action: 'delete-draft' });
    }
  };

  /** Save draft with 2s debounce. Fire-and-forget. */
  const saveDraft = useCallback(
    (method: DiscoveryMethod, data: Record<string, unknown>, assessmentId?: string | null) => {
      if (!user) return;

      pendingSave.current = { method, data, assessmentId };

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        if (pendingSave.current) {
          upsertDraft(user.id, method, data, assessmentId);
          // Optimistic local update
          setDraft(prev => ({
            id: prev?.id ?? '',
            method,
            draft_data: data,
            assessment_id: assessmentId ?? prev?.assessment_id ?? null,
            created_at: prev?.created_at ?? new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }));
          pendingSave.current = null;
        }
      }, DEBOUNCE_MS);
    },
    [user]
  );

  /** Save draft immediately, bypassing debounce. */
  const saveDraftImmediate = useCallback(
    async (method: DiscoveryMethod, data: Record<string, unknown>, assessmentId?: string | null) => {
      if (!user) return;

      // Cancel any pending debounced save
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      pendingSave.current = null;

      await upsertDraft(user.id, method, data, assessmentId);
      setDraft(prev => ({
        id: prev?.id ?? '',
        method,
        draft_data: data,
        assessment_id: assessmentId ?? prev?.assessment_id ?? null,
        created_at: prev?.created_at ?? new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));
    },
    [user]
  );

  /** Delete draft (on completion or "Start Fresh"). */
  const deleteDraft = useCallback(async () => {
    if (!user) return;

    // Cancel any pending debounced save
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    pendingSave.current = null;

    setDraft(null);
    await deleteDraftFromDB(user.id);
  }, [user]);

  return {
    draft,
    isLoading,
    saveDraft,
    saveDraftImmediate,
    deleteDraft,
  };
};
